import * as C from '@radix-ui/react-collapsible';
import { type ComponentPropsWithoutRef, createContext, useContext, useLayoutEffect, useRef, useState } from 'react';

import './ui.css';

const Fold = createContext<{ open: boolean; set: (open: boolean) => void } | null>(null);

/**
 * A fold (Activity, Stats' "Every … time", Privacy's list, any show/hide): Radix Collapsible,
 * so the trigger is a button that says whether it's expanded and what it controls.
 * Controlled (`open`, `onOpenChange`) or not (`defaultOpen`). Unstyled: the trigger and the
 * content take their screen's classes (a chevron turns on `[aria-expanded='true']`).
 *
 * Closed, the content stays in the page but hidden, and where the browser can (`hidden=
 * "until-found"`), find-in-page still finds the words in it and opens the fold to show them.
 *
 *   <Collapsible open={open} onOpenChange={setOpen}>
 *     <CollapsibleTrigger className="act-toggle">Activity <Chevron /></CollapsibleTrigger>
 *     <CollapsibleContent>…</CollapsibleContent>
 *   </Collapsible>
 */
export function Collapsible({ open, defaultOpen = false, onOpenChange, ...props }: ComponentPropsWithoutRef<typeof C.Root>) {
	const [own, setOwn] = useState(defaultOpen);
	const shown = open ?? own;
	const set = (next: boolean) => {
		if (open === undefined) setOwn(next);
		onOpenChange?.(next);
	};
	return (
		<Fold.Provider value={{ open: shown, set }}>
			<C.Root {...props} open={shown} onOpenChange={set} />
		</Fold.Provider>
	);
}

/** The button that folds and unfolds it (a real `<button>`; `asChild` to use your own). */
export const CollapsibleTrigger = C.Trigger;

/** Find-in-page into closed content (Chrome, Edge, Firefox 139+, Safari 26): where it isn't, plain `hidden`. */
const untilFound = () => typeof document !== 'undefined' && 'onbeforematch' in document.body;

/**
 * The folded content (ui.css `.ui-fold`). Always in the page: closed, it's `hidden=
 * "until-found"` where the browser supports it (a find match opens the fold), else `hidden`.
 */
export function CollapsibleContent({ className, ...props }: Omit<ComponentPropsWithoutRef<typeof C.Content>, 'forceMount' | 'hidden'>) {
	const fold = useContext(Fold);
	const ref = useRef<HTMLDivElement>(null);
	const open = fold?.open ?? false;
	const setRef = useRef(fold?.set);
	setRef.current = fold?.set;
	// React doesn't know hidden="until-found" (or beforematch): set both by hand.
	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (open) el.removeAttribute('hidden');
		else el.setAttribute('hidden', untilFound() ? 'until-found' : '');
	}, [open]);
	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		const found = () => setRef.current?.(true);
		el.addEventListener('beforematch', found);
		return () => el.removeEventListener('beforematch', found);
	}, []);
	return <C.Content {...props} ref={ref} forceMount hidden={undefined} className={className ? `ui-fold ${className}` : 'ui-fold'} />;
}
